import { Suspense, lazy } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { Button } from "@atoms"

import { Dialog } from "@molecules"
import DialogFooter from "@molecules/Dialog/DialogFooter/DialogFooter"
import DialogHeader from "@molecules/Dialog/DialogHeader/DialogHeader"

import styles from "./CodeScannerDialog.module.scss"
import { CodeScannerDialogProps } from "./CodeScannerDialog.types"
import CodeScannerErrorBoundary from "./CodeScannerErrorBoundary"

// The scanner and its decoder load on first use, so pages that never scan don't carry them
const CodeScanner = lazy(() => import("@pages/Pricebook/components/CodeScanner/CodeScanner"))

/** Start loading the scanner before the user presses the button that opens it. */
export function preloadCodeScanner(): void {
    void import("@pages/Pricebook/components/CodeScanner/CodeScanner")
}

function CodeScannerDialog(props: CodeScannerDialogProps) {
    const { isOpen, onOpenChange, title, onCode, isScannerPaused = false, result, actions } = props
    const isDesktop = useIsDesktop()

    return (
        <Dialog.Root
            mode="modal"
            variant={isDesktop ? "default" : "focused"}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
        >
            <Dialog.Content className={styles.dialogContent}>
                <DialogHeader.Root>
                    <DialogHeader.Title title={title} />
                    {isDesktop && <DialogHeader.CloseButton onClick={() => onOpenChange(false)} />}
                </DialogHeader.Root>
                <Dialog.Body className={styles.body}>
                    <CodeScannerErrorBoundary>
                        <Suspense fallback={<div className={styles.placeholder}>Preparing the scanner…</div>}>
                            {isOpen && <CodeScanner onCode={onCode} isPaused={isScannerPaused} />}
                        </Suspense>
                    </CodeScannerErrorBoundary>
                    {result}
                </Dialog.Body>
                <DialogFooter.Root>
                    <DialogFooter.LeftSide>
                        <Button
                            type="button"
                            variant="ghost"
                            size="lg"
                            onClick={() => onOpenChange(false)}
                            noDebounce={true}
                        >
                            Cancel
                        </Button>
                    </DialogFooter.LeftSide>
                    {actions && <DialogFooter.RightSide>{actions}</DialogFooter.RightSide>}
                </DialogFooter.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}

export default CodeScannerDialog
